/**
 * Devices API adapter layer.
 * Wraps the BFF-based device.api.ts functions and maps to the rich Device type
 * used by the original UI components.
 */
import type { CursorParams, CursorPaginatedResponse } from '../types/api.types'
import type { Device, DevicePingInfo, Geolocation } from '../types/device.types'
import type { Device as BffDevice, LockDeviceRequest, SendMessageRequest, CommandResponse } from './device.types'
import {
  listDevices,
  getDevice,
  lockDevice as bffLockDevice,
  unlockDevice as bffUnlockDevice,
  sendDeviceMessage,
  wipeDevice as bffWipeDevice,
} from './device.api'

/**
 * Map from BFF camelCase Device to rich UI Device with derived fields.
 */
function mapBffDevice(bff: BffDevice): Device {
  const pingInfo = bff.lastPingInfo as unknown as DevicePingInfo | null
  const pingStatus = bff.lastPingStatus as Record<string, unknown> | undefined

  let geolocation: Geolocation | null = null
  if (pingStatus?.geolocation) {
    const geo = pingStatus.geolocation as Record<string, unknown>
    if (typeof geo.lat === 'number' && typeof geo.lon === 'number') {
      geolocation = { lat: geo.lat, lon: geo.lon }
    }
  }

  const ipAddress = (pingStatus?.ipAddress as string) || pingInfo?.PublicIpAddress || ''

  return {
    device_id: bff.deviceId,
    ipAddress,
    isOnline: bff.isOnline,
    onlineDuration: bff.onlineDuration || '',
    lastPingAt: bff.lastPingAt || null,
    geolocation,
    createdAt: bff.createdAt,
    updatedAt: bff.updatedAt,
    registeredAt: bff.registeredAt || null,
    activatedAt: bff.activatedAt || null,
    pingInfo,
    status: bff.status,
    latestCommandStatus: bff.latestCommandStatus,
  }
}

export async function listCursor(params?: CursorParams): Promise<CursorPaginatedResponse<Device>> {
  const resp = await listDevices({
    limit: params?.pageSize ?? 50,
    cursor: params?.cursor,
    search: params?.search,
  })
  return {
    items: resp.data.map(mapBffDevice),
    totalCount: 0,
    limit: resp.pagination.limit,
    nextCursor: resp.pagination.nextCursor,
    prevCursor: resp.pagination.prevCursor,
    hasNext: resp.pagination.hasMore,
    hasPrev: !!resp.pagination.prevCursor,
  }
}

export async function getById(deviceId: string): Promise<Device> {
  const bff = await getDevice(deviceId)
  return mapBffDevice(bff)
}

export async function deleteDevice(_deviceId: string): Promise<void> {
  // The BFF does not currently expose a delete endpoint. No-op for now.
  console.warn('deleteDevice is not implemented in the BFF')
}

export async function lockDevice(
  deviceId: string,
  request: LockDeviceRequest
): Promise<CommandResponse> {
  return bffLockDevice(deviceId, request)
}

export async function unlockDevice(
  deviceId: string,
  request: { message?: string } = {}
): Promise<CommandResponse> {
  return bffUnlockDevice(deviceId, request)
}

export async function sendMessage(
  deviceId: string,
  request: SendMessageRequest
): Promise<CommandResponse> {
  return sendDeviceMessage(deviceId, request)
}

export async function wipeDevice(deviceId: string): Promise<CommandResponse> {
  return bffWipeDevice(deviceId)
}
